import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { setAnalyticsConsent } from '../lib/analytics';

const STORAGE_KEY = 'vastra_cookie_consent';

type ConsentStatus = 'pending' | 'accepted' | 'dismissed';

export const CookieConsent: React.FC = () => {
  const [status, setStatus] = useState<ConsentStatus>('pending');
  const dialogRef = useRef<HTMLDivElement>(null);
  const acceptButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as ConsentStatus | null;
      if (stored === 'accepted' || stored === 'dismissed') setStatus(stored);
    } catch {
      setStatus('dismissed');
    }
  }, []);

  const pending = status === 'pending';
  useFocusTrap(pending, dialogRef, { initialFocusRef: acceptButtonRef });

  const accept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'accepted');
    } catch {
      // ignore
    }
    setAnalyticsConsent(true);
    setStatus('accepted');
  };

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'dismissed');
    } catch {
      // ignore
    }
    setAnalyticsConsent(false);
    setStatus('dismissed');
  };

  if (!pending) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-heading"
      aria-describedby="cookie-consent-message"
      className="fixed bottom-0 left-0 right-0 z-[90] bg-stone-900 text-stone-200 px-6 py-4 shadow-lg border-t border-stone-700"
    >
      <h2 id="cookie-consent-heading" className="sr-only">
        Cookie consent
      </h2>
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p id="cookie-consent-message" className="text-sm">
          We use essential cookies to run the site. By continuing you agree to our{' '}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-white transition-colors">
            Privacy Policy
          </Link>
          {' '}and{' '}
          <Link to="/terms" className="underline underline-offset-2 hover:text-white transition-colors">
            Terms & Conditions
          </Link>
          .
        </p>
        {/* Primary action first in DOM for Tab order; flex-row-reverse keeps Accept on the right visually */}
        <div className="flex flex-row-reverse items-center justify-end gap-3 flex-shrink-0 sm:flex-row sm:justify-end">
          <button
            ref={acceptButtonRef}
            type="button"
            onClick={accept}
            className="px-4 py-2 text-sm font-medium bg-white text-stone-900 rounded hover:bg-stone-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-stone-900 focus:ring-white"
          >
            Accept
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="text-sm text-stone-400 hover:text-stone-200 transition-colors underline underline-offset-2"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};
